import React, { type FC, type MouseEvent, useEffect, useState } from 'react';
import classnames from 'classnames';
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import { Printer } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';

import { useDeleteShoppingListTypesFavoriteProduct, usePostShoppingListTypesAddProduct } from 'api';
import { IProduct } from 'api/types';
import { useNotifications } from 'hooks';

import { ReactComponent as MessageQuestionIcon } from 'assets/messages-question.svg';

import styles from 'theme/components/wise/WisePdpActionButton/WisePdpActionButton.module.scss';

export type WiseActionButtonProps =
  | {
      variant: 'print';
      className?: string;
      onClick?: () => void;
    }
  | {
      variant: 'askProduct';
      product?: IProduct;
      className?: string;
      onClick?: () => void;
    }
  | {
      variant: 'favourite';
      product: IProduct;
      unitId: number;
      className?: string;
    };

type WiseActionButtonFavouriteProps = {
  product: IProduct;
  unitId: number;
  rootClass: string;
};

const WiseActionButtonFavourite: FC<WiseActionButtonFavouriteProps> = ({
  product,
  unitId,
  rootClass
}) => {
  const { t } = useTranslation();
  const { showErrorMessage } = useNotifications();

  const serverInFavourite = Boolean(
    product.product_shopping_list_full_info?.in_favourite_shopping_list
  );
  const [inFavourite, setInFavourite] = useState(serverInFavourite);

  useEffect(() => {
    setInFavourite(serverInFavourite);
  }, [serverInFavourite, product.id]);

  const { mutate: addToFavouritesMutate, isLoading: isAddingToFavourites } =
    usePostShoppingListTypesAddProduct({
      onSuccess: () => {
        setInFavourite(true);
      },
      onError: () => {
        showErrorMessage(t('Nie udało się dodać produktu do ulubionych'));
      }
    });

  const { mutate: removeFromFavouritesMutate, isLoading: isRemovingFromFavourites } =
    useDeleteShoppingListTypesFavoriteProduct({
      onSuccess: () => {
        setInFavourite(false);
      },
      onError: () => {
        showErrorMessage(t('Nie udało się usunąć produktu z ulubionych'));
      }
    });

  const isPending = isAddingToFavourites || isRemovingFromFavourites;

  const runToggle = () => {
    if (!product?.id || !unitId || isPending) {
      return;
    }
    if (inFavourite) {
      removeFromFavouritesMutate({ productId: product.id, unitId });
    } else {
      addToFavouritesMutate({
        productId: product.id,
        unitId,
        quantity: 1,
        type: 'FAVORITES'
      });
    }
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    runToggle();
  };

  const label = inFavourite ? t('Usuń z ulubionych') : t('Dodaj do ulubionych');

  return (
    <div
      className={classnames(
        rootClass,
        isPending && styles.actionButtonPending,
        'StylePath-Components-Wise-WiseActionButton-favourite'
      )}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!isPending) {
            runToggle();
          }
        }
      }}
      role="button"
      tabIndex={isPending ? -1 : 0}
      aria-label={label}
      aria-busy={isPending}>
      {inFavourite ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
      <span>{label}</span>
    </div>
  );
};

const WiseActionButton: FC<WiseActionButtonProps> = (props) => {
  const { showWarningMessage } = useNotifications();

  const rootClass = classnames(styles.actionButton, props.className);

  if (props.variant === 'print') {
    return (
      <div
        className={classnames(rootClass, 'StylePath-Components-Wise-WiseActionButton-print')}
        onClick={() => (props.onClick ? props.onClick() : showWarningMessage('Funkcja niezaimplementowana'))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            props.onClick ? props.onClick() : showWarningMessage('Funkcja niezaimplementowana');
          }
        }}
        role="button"
        tabIndex={0}>
        <Printer />
        <span>
          <Trans>Drukuj</Trans>
        </span>
      </div>
    );
  }

  if (props.variant === 'favourite') {
    return (
      <WiseActionButtonFavourite
        product={props.product}
        unitId={props.unitId}
        rootClass={rootClass}
      />
    );
  }

  return (
    <div
      className={classnames(rootClass, 'StylePath-Components-Wise-WiseActionButton-askProduct')}
      onClick={props.onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          props.onClick?.();
        }
      }}
      role="button"
      tabIndex={0}>
      <MessageQuestionIcon />
      <span>
        <Trans>Zapytaj</Trans>
      </span>
    </div>
  );
};

export default WiseActionButton;
